const getApiUrl = () => {
  let url = import.meta.env.VITE_API_URL || 'http://localhost:4100/api/v1'
  if (url && !url.startsWith('http') && !url.startsWith('/')) {
    url = `https://${url}`
  }
  url = url.replace(/\/$/, '')
  if (!url.endsWith('/api/v1')) {
    url = `${url}/api/v1`
  }
  return url
}
const API_URL = getApiUrl()
export { API_URL }

export interface StorefrontSettings {
  storeName: string
  logo?: string
  email: string
  phone: string
  address: string
  city: string
  postalCode: string
  country: string
  website?: string
  webshop: {
    enabled: boolean
    storeName?: string
    currency: string
    primaryColor?: string
    freeShippingThreshold: number
    shippingCost: number
    returnPolicy: string | { en: string; mk: string; al: string }
    paymentMethods: string[]
    heroTitle: string | { en: string; mk: string; al: string }
    heroSubtitle: string | { en: string; mk: string; al: string }
    aboutContent: string | { en: string; mk: string; al: string }
    contactEmail: string
    contactPhone: string
    socialMedia: {
      facebook?: string
      instagram?: string
      twitter?: string
      linkedin?: string
    }
    allowGuestCheckout?: boolean
    requireEmailVerification?: boolean
    enableReviews?: boolean
    enableWishlist?: boolean
    enableLoyaltyProgram?: boolean
  }
}

export interface Category {
  id: string
  name: string
  slug: string
  description?: string
  icon?: string
  productCount: number
}

export interface Product {
  id: string
  name: string
  sku: string
  description?: string
  imageUrl?: string
  sellingPrice: number
  compareAtPrice?: number
  taxRate: number
  category?: {
    id: string
    name: string
    slug: string
  }
  inStock: boolean
  stockQuantity?: number
}

export interface ProductDetail extends Product {
  purchasePrice: number
  barcode?: string
  shortDescription?: string
  images?: string[]
  featuredImage?: string
  unit?: string
  stocks?: Array<{
    location: string
    quantity: number
  }>
  createdAt?: string
  updatedAt?: string
}

export async function getStorefrontSettings(): Promise<StorefrontSettings | null> {
  try {
    const response = await fetch(`${API_URL}/storefront/settings`)
    const data = await response.json()
    return data.success ? data.data : null
  } catch (error) {
    console.error('Error fetching storefront settings:', error)
    return null
  }
}

export async function getCategories(locale?: string): Promise<Category[]> {
  try {
    const localeParam = locale || localStorage.getItem('storefront-locale') || 'mk'
    const response = await fetch(`${API_URL}/storefront/categories?locale=${localeParam}`)
    const data = await response.json()
    return data.success ? data.data : []
  } catch (error) {
    console.error('Error fetching categories:', error)
    return []
  }
}

export interface ProductsResponse {
  items: Product[]
  meta: {
    page: number
    perPage: number
    total: number
    totalPages: number
  }
}

export interface ProductsQuery {
  page?: number
  perPage?: number
  category?: string
  categoryId?: string
  search?: string
  minPrice?: number
  maxPrice?: number
  sortBy?: 'name' | 'price' | 'createdAt'
  sortOrder?: 'asc' | 'desc'
}

export async function getProducts(query: ProductsQuery = {}): Promise<ProductsResponse | null> {
  try {
    const params = new URLSearchParams()
    if (query.page) params.append('page', String(query.page))
    if (query.perPage) params.append('perPage', String(query.perPage))
    if (query.categoryId) params.append('categoryId', query.categoryId)
    if (query.category) params.append('category', query.category)
    if (query.search) params.append('search', query.search)
    if (query.minPrice) params.append('minPrice', String(query.minPrice))
    if (query.maxPrice) params.append('maxPrice', String(query.maxPrice))
    if (query.sortBy) params.append('sortBy', query.sortBy)
    if (query.sortOrder) params.append('sortOrder', query.sortOrder)

    const response = await fetch(`${API_URL}/storefront/products?${params}`)
    const data = await response.json()
    return data.success ? data.data : null
  } catch (error) {
    console.error('Error fetching products:', error)
    return null
  }
}

export async function getFeaturedProducts(limit: number = 8): Promise<Product[]> {
  try {
    const response = await fetch(`${API_URL}/storefront/products/featured?limit=${limit}`)
    const data = await response.json()
    return data.success ? data.data : []
  } catch (error) {
    console.error('Error fetching featured products:', error)
    return []
  }
}

export async function getProduct(id: string): Promise<ProductDetail | null> {
  try {
    const locale = localStorage.getItem('storefront-locale') || 'mk'
    const response = await fetch(`${API_URL}/storefront/products/${id}?locale=${locale}`)
    const data = await response.json()
    return data.success ? data.data : null
  } catch (error) {
    console.error('Error fetching product:', error)
    return null
  }
}

export interface OrderItem {
  productId: string
  quantity: number
  price: number
}

export interface CreateOrderData {
  customerName: string
  customerEmail: string
  customerPhone: string
  shippingAddress: string
  shippingCity: string
  shippingPostalCode: string
  paymentMethod: 'cash' | 'card' | 'bank_transfer' | 'mobile_payment'
  items: OrderItem[]
  notes?: string
}

export interface CreateOrderResponse {
  orderId: string
  orderNumber: string
  total: number
}

export interface RegisterData {
  firstName: string
  lastName: string
  email: string
  phone: string
  password?: string
  marketingConsent?: boolean
}

export interface LoginData {
  email: string
  password?: string
}

export interface LoginResponse {
  accessToken: string
  refreshToken: string
  user: {
    id: string
    name: string
    email: string
  }
}

export interface RegisterResponse {
  id: string
  customerNumber: string
  firstName: string
  lastName: string
  email: string
  phone: string
}

export async function login(data: LoginData): Promise<LoginResponse | null> {
  try {
    const response = await fetch(`${API_URL}/storefront/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    const result = await response.json()
    if (!result.success) {
      throw new Error(result.error?.message || 'Failed to login')
    }
    return result.data
  } catch (error) {
    console.error('Error logging in:', error)
    throw error
  }
}

export async function register(data: RegisterData): Promise<RegisterResponse | null> {
  try {
    const response = await fetch(`${API_URL}/storefront/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    const result = await response.json()
    if (!result.success) {
      throw new Error(result.error?.message || 'Failed to register')
    }
    return result.data
  } catch (error) {
    console.error('Error registering:', error)
    throw error
  }
}

export async function createOrder(orderData: CreateOrderData): Promise<CreateOrderResponse | null> {
  try {
    const response = await fetch(`${API_URL}/storefront/orders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(orderData),
    })
    const data = await response.json()
    if (!data.success) {
      throw new Error(data.error?.message || 'Failed to create order')
    }
    return data.data
  } catch (error) {
    console.error('Error creating order:', error)
    throw error
  }
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('sf_token') || sessionStorage.getItem('sf_token')
  return token ? { 'Authorization': `Bearer ${token}` } : {}
}

export interface Address {
  id: string
  name?: string
  street: string
  city: string
  postalCode: string
  country: string
  phone?: string
  isDefault: boolean
}

export interface ProfileUpdateData {
  firstName: string
  lastName: string
  phone?: string
  notifyByEmail?: boolean
  notifyBySMS?: boolean
  notifyByWhatsApp?: boolean
}

export interface ChangePasswordData {
  currentPassword: string
  newPassword: string
}

export interface OnlineOrder {
  id: string
  orderNumber: string
  status: string
  paymentStatus: string
  total: number
  subtotal: number
  taxAmount: number
  shippingCost: number
  createdAt: string
  items: Array<{
    id: string
    name: string
    unitPrice: number
    quantity: number
    total: number
    product: {
      name: string
      imageUrl?: string
    }
  }>
}

export async function getMe(): Promise<any> {
  const headers = authHeaders()
  if (!headers['Authorization']) return null

  try {
    const response = await fetch(`${API_URL}/storefront/me`, { headers })
    const result = await response.json()
    return result.success ? result.data : null
  } catch (error) {
    return null
  }
}

export async function updateProfile(data: ProfileUpdateData): Promise<any> {
  const headers = authHeaders()
  const response = await fetch(`${API_URL}/storefront/profile`, {
    method: 'PUT',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  const result = await response.json()
  if (!result.success) throw new Error(result.error?.message || 'Failed to update profile')
  return result.data
}

export async function changePassword(data: ChangePasswordData): Promise<any> {
  const headers = authHeaders()
  const response = await fetch(`${API_URL}/storefront/change-password`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  const result = await response.json()
  if (!result.success) throw new Error(result.error?.message || 'Failed to change password')
  return result
}

export async function getAddresses(): Promise<Address[]> {
  const headers = authHeaders()
  const response = await fetch(`${API_URL}/storefront/addresses`, { headers })
  const result = await response.json()
  return result.success ? result.data : []
}

export async function getMyOrders(): Promise<OnlineOrder[]> {
  const headers = authHeaders()
  if (!headers['Authorization']) return []

  try {
    const response = await fetch(`${API_URL}/storefront/profile/orders`, { headers })
    const result = await response.json()
    return result.success ? result.data : []
  } catch (error) {
    console.error('Error fetching orders:', error)
    return []
  }
}

export async function createAddress(data: Omit<Address, 'id'>): Promise<Address> {
  const headers = authHeaders()
  const response = await fetch(`${API_URL}/storefront/addresses`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  const result = await response.json()
  if (!result.success) throw new Error(result.error?.message || 'Failed to create address')
  return result.data
}

export async function updateAddress(id: string, data: Partial<Address>): Promise<Address> {
  const headers = authHeaders()
  const response = await fetch(`${API_URL}/storefront/addresses/${id}`, {
    method: 'PUT',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  const result = await response.json()
  if (!result.success) throw new Error(result.error?.message || 'Failed to update address')
  return result.data
}

export async function deleteAddress(id: string): Promise<void> {
  const headers = authHeaders()
  const response = await fetch(`${API_URL}/storefront/addresses/${id}`, {
    method: 'DELETE',
    headers,
  })
  const result = await response.json()
  if (!result.success) throw new Error(result.error?.message || 'Failed to delete address')
}

export interface ContactMessage {
  name: string
  email: string
  phone?: string
  subject?: string
  message: string
}

export async function sendContactMessage(data: ContactMessage): Promise<void> {
  const response = await fetch(`${API_URL}/storefront/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  const result = await response.json()
  if (!result.success) throw new Error(result.error?.message || 'Failed to send message')
}

export async function cancelOrder(id: string): Promise<void> {
  const headers = authHeaders()
  const response = await fetch(`${API_URL}/storefront/orders/${id}/cancel`, {
    method: 'POST',
    headers,
  })
  const result = await response.json()
  if (!result.success) throw new Error(result.error?.message || 'Failed to cancel order')
}
